/* eslint-disable arrow-body-style */
import React, { useEffect, useMemo } from 'react';
import _ from 'lodash';
import { object } from 'prop-types';
import { graphql, navigate, Script, useStaticQuery } from 'gatsby';
import { createAffirmComponent } from '../../../utils/affirm.js';
import { useIsClient, useIsomorphicLayoutEffect } from '../../../hooks';
import { buttonLengthConstants, buttonVariantConstants } from '../../../common/constants';
import { useRootState } from '../../../context/root-state.context';
import Row from '../../FlexboxGrid/Row.jsx';
import Col from '../../FlexboxGrid/Col.jsx';
import HorizontalScroller from '../Carousels/HorizontalScroller';
import ProductVariantAffirm from './ProductVariantAffirm';
import LooxRating from './LooxRating';
import { renderRichTextReact } from '../../../utils/storyblokRichText';
import ButtonRegular from '../../Button/ButtonRegular.jsx';
import ProductVariantSelector from './ProductVariantSelector.jsx';

// eslint-disable-next-line arrow-body-style
const TwoColumnProductHighlight = ({ blok }) => {
  const isClient = useIsClient();
  const { shopifyProduct, setShopifyProduct, productAffirms, setProductAffirms } = useRootState();

  // query product variants from Shopify
  const productVariantsQuery = useStaticQuery(graphql`
    {
      allShopifyProductVariant {
        edges {
          node {
            product {
              handle
              shopifyId
              storefrontId
              title
            }
            price
            storefrontId
            title
          }
        }
      }
    }
  `);

  // filter product variants by the storefrontId from SB
  const selectedProductVariant = useMemo(() => {
    const variantStorefrontID = blok.productVariant;

    if (!_.isEmpty(productVariantsQuery.allShopifyProductVariant)) {
      const { node } = productVariantsQuery.allShopifyProductVariant.edges
        .filter((edge) => {
          return edge.node.storefrontId === variantStorefrontID;
        })
        .find((edge) => edge.node);

      return node;
    }

    return null;
  }, [blok, productVariantsQuery]);

  // set variant color to Star Wars for ProductVariantSelector bg image
  useEffect(() => {
    if (blok.theme && selectedProductVariant) {
      if (blok.theme === 'starWars') {
        selectedProductVariant.color = 'starWars';
      }
    }
  }, [blok, selectedProductVariant]);

  useEffect(() => {
    setShopifyProduct(selectedProductVariant);
  }, [selectedProductVariant]);

  useIsomorphicLayoutEffect(() => {
    if (isClient && window.affirm && window.affirm.jsReady() && window.affirm.ui.ready() && shopifyProduct) {
      const newProductAffirms = { ...productAffirms };
      const slug = _.kebabCase(selectedProductVariant.handle);
      const affirmHomepageComponent = createAffirmComponent('homepage', selectedProductVariant.price || 0, false);
      newProductAffirms[`homepage-affirm-${slug}`] = affirmHomepageComponent;

      const variantTitle = _.kebabCase(selectedProductVariant.title);
      const affirmProductComponent = createAffirmComponent('product', selectedProductVariant.price || 0);
      newProductAffirms[`product-affirm-${slug}-${variantTitle}`] = affirmProductComponent;

      setProductAffirms(newProductAffirms);
    }
  }, [isClient, selectedProductVariant]);

  return (
    <>
      <Row className="max-w-[100%]">
        {/* left column */}
        <Col xs={8} md={6} lg={7}>
          {!_.isEmpty(blok.columnLeftGallery) && (
            <HorizontalScroller images={blok.columnLeftGallery} imageWidth="380px" />
          )}
        </Col>

        {/* right column */}
        <Col xs={8} md={3} lg={4} className="md:ml-[10%] lg:ml-[8.333333333333334%] relative">
          {/* absolutely positioned logo */}
          {selectedProductVariant && !_.isEmpty(blok.logo) && (
            <div className="mt-[30px] md:mt-[0] mb-[10px]">
              <img
                alt={blok?.logo?.find((el) => el)?.assetLg?.alt}
                className="w-[80px] sm:w-[100px] md:w-[110px] lg:w-[150px]"
                src={blok?.logo?.find((el) => el)?.assetLg?.filename}
              />
            </div>
          )}

          {/* product name */}
          {selectedProductVariant && (
            <h2 className="typography-body-xxl font-bold text-beige-100">{selectedProductVariant.product?.title}</h2>
          )}

          {/* product price */}
          {selectedProductVariant && (
            <p className="typography-body-xxl font-bold text-beige-100">
              ${selectedProductVariant.price.toLocaleString()}
            </p>
          )}

          {/* product variant selector */}
          {selectedProductVariant && (
            <div className="mt-[24px]">
              <ProductVariantSelector
                currentVariant={selectedProductVariant}
                productVariants={[selectedProductVariant]}
              />
            </div>
          )}

          {/* variant name */}
          {selectedProductVariant && (
            <h2 className="typography-eyebrow-xs text-beige-100">{selectedProductVariant.title}</h2>
          )}

          {/* product description */}
          <div className="py-[30px] typography-body-l !font-utopiaStdCaption">
            {blok.description && renderRichTextReact(blok.description)}
          </div>

          {/* button */}
          {blok.linkText && blok.linkURL && (
            <div>
              <ButtonRegular
                length={buttonLengthConstants.NORMAL}
                onClick={() => navigate(blok.linkURL)}
                theme={blok.theme}
                variant={buttonVariantConstants.PRIMARY_ORANGE}
              >
                {blok.linkText}
              </ButtonRegular>
            </div>
          )}

          {/* product affirm */}
          {selectedProductVariant && blok.displayAffirm && (
            <div className="[&_.variantAffirm]:typography-body-s [&_.variantAffirm]:text-beige-100 mt-[24px]">
              <ProductVariantAffirm currentVariant={selectedProductVariant} starWarsTheme={blok.theme === 'starWars'} />
            </div>
          )}

          {/* product review */}
          {selectedProductVariant && blok.displayRating && (
            <div>
              <LooxRating link={blok.linkURL} shopifyId={selectedProductVariant.product?.shopifyId} />
            </div>
          )}
        </Col>
      </Row>
      <Script async src="https://loox.io/widget/loox.js?shop=piaggiofastforward.myshopify.com" />
    </>
  );
};

TwoColumnProductHighlight.propTypes = {
  blok: object.isRequired,
};

export default TwoColumnProductHighlight;
